<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import axios from 'axios'
import { Plus, Search, Shapes, SlidersHorizontal } from 'lucide-vue-next'

import AudienceTemplates from '@/views/audience/components/AudienceTemplates.vue'
import CreateAudienceModal from '@/views/audience/components/CreateAudienceModal.vue'
import AudienceStoreChips from '@/views/audience/components/AudienceStoreChips.vue'
import { parseStoreIdsFromQuery, resolveScopedStoreIds } from '@/utils/audienceStoreIds'
import useContentScopeList, {
  useContentScopeCriteria,
  useContentScopeSearch,
  useContentScopeStatusCriterion
} from '@/composables/contentScopeList'
import { getRowContentCreator, getRowContentScope, splitContentScopeListParams } from '@/utils/contentScope'

import type { Audience } from '@/types/types'
import type { ContentScopeFilters } from '@/types/contentScope'

type AudienceListRow = Audience & { created_at?: string }

const { fetchMerchantAudiences, segmentTypes } = useAudience()
const { stores } = useLocalStorage()
const { localDate } = useDate()
const { openInNewTab } = useDOM()
const { t } = useI18n()
const { formatNumber } = useNumber()
const route = useRoute()
const router = useRouter()

const props = withDefaults(
  defineProps<{
    segmentName?: string | null
  }>(),
  {
    segmentName: ''
  }
)

const isLoading = ref(true)
const isFetching = ref(false)
const isCreateAudienceModalOpen = ref(false)

const pageTitle = t('pages.customer.audiences.header.crumbs')

const totalAudiences = ref(0)
const emptyState = {
  primary: {
    text: t('pages.customer.audiences.header.createBtn'),
    isOutlined: false,
    icon: null,
    link: null,
    callBack: () => openCreateAudienceModal()
  },
  secondary: {
    text: t('pages.customer.audiences.emptyState.secondary'),
    isOutlined: true,
    icon: 'HeroAcademicCapIconOutline',
    link: 'https://heypongohelp.zendesk.com/hc/fr/articles/22949316359569-Comment-assigner-une-liste-%C3%A0-vos-clients',
    callBack: () => openInNewTab(emptyState.secondary.link)
  },
  title: t('pages.customer.audiences.emptyState.title'),
  description: t('pages.customer.audiences.emptyState.description')
}

const storeFilter = ref<number[]>([])
const resolvedStoreFilter = computed(() => resolveScopedStoreIds(storeFilter.value, stores.value.length))

const table = reactive({
  pagination: {
    total: null,
    lastPage: null,
    pageSize: 10,
    currentPage: 1,
    page: 1
  },
  columns: {
    name: {
      id: 'name',
      label: t('pages.customer.audiences.table.columns.name'),
      type: 'customCell',
      sortable: true
    },
    type: {
      id: 'type',
      label: t('pages.customer.audiences.table.columns.type'),
      type: 'customCell',
      sortable: true
    },
    createdAt: {
      id: 'created_at',
      label: t('pages.customer.audiences.table.columns.createdAt'),
      sortable: true
    },
    origin: {
      id: 'origin',
      label: t('contentScope.origin'),
      type: 'customCell'
    },
    stores: {
      id: 'stores',
      label: t('pages.customer.audiences.table.columns.stores'),
      type: 'customCell'
    },
    members: {
      id: 'customer_count',
      label: t('pages.customer.audiences.table.columns.members'),
      type: 'customCell',
      sortable: true,
      numeric: true
    },
    creator: {
      id: 'creator',
      label: t('contentScope.filters.createdBy'),
      type: 'customCell'
    }
  },
  sort: {
    sortBy: 'created_at',
    sortOrder: 'desc'
  }
})

const audienceList = ref([])
const segmentName = useVModel(props, 'segmentName')

const fetchAudiencesTotal = async (params: Record<string, unknown>) => {
  const { statuses, search, scopeParams } = splitContentScopeListParams(params)
  const audiencesData = await fetchMerchantAudiences({
    pagination: { pageSize: 1, page: 1 },
    search,
    type: statuses[0] ?? null,
    storeIds: resolvedStoreFilter.value,
    scopeParams
  })

  return audiencesData?.meta?.total ?? 0
}

const reloadAudiences = () => {
  table.pagination.page = 1
  table.pagination.currentPage = 1
  return initAudienceList()
}

const contentScope = useContentScopeList('audience', {
  fetchTotal: fetchAudiencesTotal,
  reload: reloadAudiences,
  countsKey: () => storeFilter.value
})

const { searchText } = useContentScopeSearch(contentScope, 500)

const { origin, createdBy } = useContentScopeCriteria(contentScope)
const typeCriterion = useContentScopeStatusCriterion(contentScope, () => ({
  label: t('pages.customer.audiences.table.columns.type'),
  icon: Shapes,
  options: segmentTypes
    .filter((type) => type.id !== 'typology')
    .map((type) => ({ value: String(type.id), label: type.text })),
  multiple: false
}))
const filterCriteria = computed(() => [origin.value, createdBy.value, typeCriterion.value])

watch(
  () => contentScope.filters.value.statuses[0] ?? null,
  (type) => {
    if (type !== (route.query.type ?? null)) updateUrl()
  }
)

let audiencesCancelTokenSource = null

const initAudienceList = async () => {
  if (audiencesCancelTokenSource) {
    audiencesCancelTokenSource.cancel('Operation canceled by the user.')
  }

  const cancelTokenSource = axios.CancelToken.source()
  audiencesCancelTokenSource = cancelTokenSource

  isFetching.value = true
  try {
    const audiencesData = await fetchMerchantAudiences({
      pagination: !contentScope.filters.value.search ? table.pagination : { pageSize: 15, page: 1 },
      sort: table.sort,
      search: contentScope.filters.value.search,
      type: contentScope.filters.value.statuses[0] ?? null,
      storeIds: resolvedStoreFilter.value,
      scopeParams: contentScope.params.value,
      cancelToken: cancelTokenSource.token
    })

    if (!audiencesData) return

    setPagination(audiencesData.meta)

    if (audiencesData.data.length > 0) {
      audienceList.value = transformAudiencesData(audiencesData)
    } else {
      audienceList.value = []
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      throw new Error(`Search canceled: ${error.message}`)
    } else {
      throw error
    }
  } finally {
    if (audiencesCancelTokenSource === cancelTokenSource) {
      isLoading.value = false
      isFetching.value = false
      audiencesCancelTokenSource = null
    }
  }
}

const transformAudiencesData = (audiencesData: { data: AudienceListRow[] }) => {
  return audiencesData.data.map((audience) => ({
    id: audience.id,
    data: {
      name: audience.name,
      type: audience.frequency_type ? 2 : audience.type === 'static' ? 0 : 1,
      stores: audience.stores ?? [],
      members: audience.customer_count,
      isProcessing: audience.is_processing,
      createdAt: localDate(audience.created_at),
      scope: audience.scope ?? null,
      creator: audience.creator ?? null
    }
  }))
}

const isFiltered = computed(
  () => Boolean(searchText.value) || Boolean(resolvedStoreFilter.value) || contentScope.hasActiveFilters.value
)

const audienceCount = computed(() => {
  const filtered = table.pagination.total ?? 0
  return { filtered, total: totalAudiences.value || filtered }
})

const setPagination = (request) => {
  table.pagination.total = request?.total
  if (!isFiltered.value) totalAudiences.value = request?.total ?? 0
  table.pagination.lastPage = request?.last_page
  table.pagination.currentPage = request?.current_page
  table.pagination.pageSize = request?.per_page
}

const openCreateAudienceModal = () => {
  isCreateAudienceModalOpen.value = true
}

const closeCreateAudienceModal = () => {
  isCreateAudienceModalOpen.value = false
}

const updateUrl = () => {
  router.push({
    path: '/audiences',
    query: {
      type: contentScope.filters.value.statuses[0] ?? null,
      search: contentScope.filters.value.search || null,
      storeIds: storeFilter.value.length ? storeFilter.value.join(',') : null,
      page: table.pagination.page,
      pageSize: table.pagination.pageSize,
      sortOrder: table.sort.sortOrder,
      sortBy: table.sort.sortBy
    }
  })
}

const handleEmptyStateActions = (button) => {
  if (button === 'secondaryButton') {
    emptyState.secondary.callBack()
  } else {
    emptyState.primary.callBack()
  }
}

const resetFilters = () => {
  storeFilter.value = []
  table.pagination.page = 1
  table.pagination.currentPage = 1
  searchText.value = ''
  const reloadsOnReset = contentScope.hasActiveFilters.value
  contentScope.resetFilters()
  if (!reloadsOnReset) initAudienceList()
  updateUrl()
}

const applyStoreFilter = async (storeIds: number[]) => {
  storeFilter.value = storeIds
  table.pagination.page = 1
  table.pagination.currentPage = 1
  await initAudienceList()
  updateUrl()
}

const changePage = (page) => {
  table.pagination.page = page
  table.pagination.currentPage = page
  initAudienceList()
  updateUrl()
}

const changeSort = (row: any) => {
  if (row.sortBy === table.sort.sortBy) {
    table.sort.sortOrder = table.sort.sortOrder === 'desc' ? 'asc' : 'desc'
  } else {
    table.sort.sortOrder = 'desc'
  }

  table.sort.sortBy = row.sortBy
  updateUrl()
  initAudienceList()
}

const goToDetails = ({ id }) => {
  router.push({ name: 'AudienceDetail', params: { audienceId: id } })
}

const getParamsFromUrl = async () => {
  const queries = route.query
  if (Object.keys(queries).length > 0) {
    table.pagination.page = parseInt(queries?.page as string) ?? 1
    table.pagination.pageSize = parseInt(queries?.pageSize as string) ?? 10
    table.sort.sortBy = (queries?.sortBy as string) ?? 'created_ad'
    table.sort.sortOrder = (queries?.sortOrder as string) ?? 'desc'
    storeFilter.value = parseStoreIdsFromQuery(queries?.storeIds) ?? []
    const queryFilters: Partial<ContentScopeFilters> = {}
    if (typeof queries.search === 'string') queryFilters.search = queries.search
    if (typeof queries.type === 'string') queryFilters.statuses = queries.type ? [queries.type] : []
    contentScope.withoutReload(() => contentScope.setFilters(queryFilters))
    await initAudienceList()
  } else {
    await initAudienceList()
  }

  if (route.hash === '#create') {
    openCreateAudienceModal()
  }
}

onMounted(async () => {
  await getParamsFromUrl()
})
</script>
<template>
  <section v-if="!isLoading">
    <TheListHeader :title="pageTitle" :count="audienceCount" unit-key="global.words.audience">
      <template #actions>
        <Button type="button" variant="default-primary" class="w-full sm:w-auto" @click="openCreateAudienceModal">
          <Plus />
          {{ t('pages.customer.audiences.header.createBtn') }}
        </Button>
      </template>
    </TheListHeader>

    <div class="px-6 pb-6 sm:px-12 sm:pb-8">
      <AudienceTemplates />
    </div>

    <div class="px-6 pb-6 sm:px-12 sm:pb-12">
      <AppTable
        v-if="isFetching || audienceList.length > 0 || isFiltered"
        :columns="table.columns"
        :rows="audienceList"
        :pagination="table.pagination"
        :is-loading="isFetching"
        :sort="table.sort"
        row-classes="cursor-pointer"
        @update:change-page="changePage"
        @update:change-sort="changeSort"
        @row-clicked="goToDetails"
      >
        <template #toolbar>
          <ListToolbar class="w-full" :criteria="filterCriteria">
            <template #leading>
              <ListSearchInput v-model="searchText" :placeholder="t('pages.customer.audiences.search.placeholder')" />
              <MultiSelectStores :model-value="storeFilter" :has-label="false" @update:model-value="applyStoreFilter" />
            </template>
          </ListToolbar>
        </template>
        <template #empty-state>
          <ContentScopeEmptyState
            v-if="contentScope.hasActiveFilters.value"
            :is-mine-view="contentScope.isMineView.value"
            :subject="contentScope.subject"
            @reset="resetFilters"
          />
          <template v-else>
            <Search class="h-8 w-8 text-muted-foreground" />
            <p class="mt-2 text-sm font-medium text-foreground-secondary">
              {{ t('pages.customer.audiences.noResults.title') }}
            </p>
            <p class="text-sm text-muted-foreground">{{ t('pages.customer.audiences.noResults.description') }}</p>
            <Button variant="outline" class="mt-3 duration-300" @click="resetFilters">
              <SlidersHorizontal class="text-primary" />
              <span class="text-primary">{{ t('contentScope.filters.resetFilters') }}</span>
            </Button>
          </template>
        </template>
        <template #td-name="{ id, data }">
          <button
            id="audienceRedirect"
            type="button"
            class="max-w-[40ch] truncate font-medium text-primary hover:text-primary/80"
            :title="data.name"
            @click="router.push({ name: 'AudienceDetail', params: { audienceId: id } })"
          >
            {{ data.name }}
          </button>
        </template>
        <template #th-origin>
          <ContentScopeColumnHeader subject="audience" />
        </template>
        <template #td-origin="{ data }">
          <ContentScopeBadge :scope="getRowContentScope(data)" />
        </template>
        <template #td-creator="{ data }">
          <ContentScopeCreator :creator="getRowContentCreator(data)" />
        </template>
        <template #td-type="{ data }">
          <BaseChip
            :color="segmentTypes[data?.type].color"
            :icon="segmentTypes[data?.type]?.icon"
            :text="segmentTypes[data?.type]?.text"
          />
        </template>
        <template #td-stores="{ data }">
          <AudienceStoreChips :stores="data.stores" />
        </template>
        <template #td-members="{ data }">
          <AppTooltip
            v-if="data?.isProcessing"
            :text="t('pages.customer.audiences.table.processing')"
            tooltip-position="top"
          >
            <Spinner />
          </AppTooltip>
          <span v-else>{{ formatNumber(data?.members) }}</span>
        </template>
      </AppTable>
      <TheEmptyState
        v-else
        class="rounded-lg border bg-white"
        :primary-button="emptyState.primary"
        :secondary-button="emptyState.secondary"
        :title="emptyState.title"
        :description="emptyState.description"
        name="HeroSquare3Stack3DIconSolid"
        @on-click="handleEmptyStateActions"
      />
    </div>
  </section>
  <TheLoadingPage v-else />

  <CreateAudienceModal
    v-model:segment-name="segmentName"
    :is-open="isCreateAudienceModalOpen"
    @close="closeCreateAudienceModal"
  />
</template>
